import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useNavigate } from 'react-router-dom';
import MobilePageLayout from '../../../components/layout/MobilePageLayout';
import DSLoading from '../../../components/designSystem/DSLoading';
import DSBottomSheet from '../../../components/designSystem/DSBottomSheet';
import { theme } from '../../../styles/theme';
import { isIndividualUserType, isOrganizationDropdownForIndividual } from '../../../Utils/userType';
import { convertToVwMob } from '../../../Utils/responsive';
import dropdownIcon from '../../../assets/Icon/project/inputbox_icon.png';
import calendarIcon from '../../../assets/Icon/project/calendar_icon.png';
import hugeIcon from '../../../assets/Icon/project/huge_icon.png';
import buildingIcon from '../../../assets/Icon/project/building_mob_icon.png';
import bankIcon from '../../../assets/Icon/project/bank_mob_icon.png';
import securityIcon from '../../../assets/Icon/project/security_mob_icon.png';
import buildingColorIcon from '../../../assets/Icon/project/building_color_icon.png';
import bankColorIcon from '../../../assets/Icon/project/bank_color_icon.png';
import securityColorIcon from '../../../assets/Icon/project/security_color_icon.png';
import profileIcon from '../../../assets/Icon/project/iconamoon_profile-fill.png';
import profileColorIcon from '../../../assets/Icon/project/iconamoon_color_profile-fill.png';
import certificateIcon from '../../../assets/Icon/project/certificate.png';
import lawIcon from '../../../assets/Icon/project/law_icon.png';
import noYellowIcon from '../../../assets/Icon/no_yellow_icon.svg';
import noYellowTabIcon from '../../../assets/Icon/no_yellow_icon_15.svg';
import deleteIcon from '../../../assets/Icon/project/delete_icno.png';
import photoIcon from '../../../assets/Icon/project/photo_photo.png';
import defaultProfileIcon from '../../../assets/Icon/project/default-profile.png';
import profileCertificateIcon from '../../../assets/Icon/project/profile_certificate.png';
import profileCertificateFilledIcon from '../../../assets/Icon/project/profile_certificate_filled.png';
import loadingLottie from '../../../assets/Icon/lottie/Insider-loading.lottie';
import { useAppSelector } from '../../../store/hooks';
import { selectAuthUser } from '../../../Module/Auth/Presentation/authSelectors';
import { BirthDatePicker } from '../../../components/common';
import DSModal from '../../../components/designSystem/DSModal';
import { useTranslation } from 'react-i18next';
import { getOrganizationDisplayName, getOrganizationOptions, ORGANIZATION_ETC_CODE } from '../../../Utils/organizationOptions';
import { getCountryOptions, getCountryLabel } from '../../../Utils/countryUtils';
import { formatUsStateLabel, US_STATES } from '../../../Utils/usStates';
import { PATHS } from '../../../routes/paths';
import {
  isCertificateImageFile,
  isImageSizeAllowed,
  MAX_CERTIFICATE_UPLOAD_BYTES,
} from '../../../Utils/imageUpload';
import {
  useMyAccount,
  isBankbookMarkedUploadedOnProfile,
  isRegistrationCardMarkedUploadedOnProfile,
  profileNeedsRegistrationCard,
} from '../useMyAccount';

/** 고정 하단 취소/저장 바 높이 — 스크롤 Main에 패딩으로 넣어 푸터가 바에 가리지 않게 함 */
const MY_ACCOUNT_MOB_MAIN_PADDING_BOTTOM = `calc(${convertToVwMob(16)} + ${convertToVwMob(50)} + ${convertToVwMob(20)} + ${convertToVwMob(16)} + env(safe-area-inset-bottom, 0px))`;

// --- Styled Components ---

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(24)};
  padding: ${convertToVwMob(8)} ${convertToVwMob(0)} ${convertToVwMob(100)};
  min-height: 100vh;
`;

const PageTitle = styled.h1`
  ${theme.typography.head.semiBold24};
  color: ${theme.colors.grayScale[90]};
  margin: 0;
`;

const StepIndicatorWrapper = styled.div`
  background-color: ${theme.colors.primaryBlue[10]};
  border: 1.6px solid ${theme.colors.primaryBlue[40]};
  border-radius: ${convertToVwMob(10)};
  padding: ${convertToVwMob(20)};
  width: 100%;
  margin-bottom: ${convertToVwMob(8)};
`;

/** 검정 안내 2줄 — 줄 사이만 간격 유지 */
const StepHintBlackBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
  width: 100%;
`;

/** 스텝 안내(검정 블록 · 파란 제목 · 프로그레스) */
const StepHintStack = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StepText = styled.p`
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[90]};
  margin: 0;
`;

/** 스텝 제목 — 프로그레스 바 바로 위 (Contents/Medium20, primary blue) */
const StepSubText = styled.p`
  font-family: Pretendard, sans-serif;
  ${theme.typography.contents.medium20};
  color: var(--primary-blue-40, ${theme.colors.primaryBlue[40]});
  margin: 0;
  margin-top: ${convertToVwMob(4)};
`;

const StepProgressRow = styled.div`
  margin-top: ${convertToVwMob(8)};
`;

const StepList = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: fit-content;
  height: ${convertToVwMob(30)};
`;

const StepLine = styled.div<{ $stepCount?: number }>`
  position: absolute;
  top: 50%;
  left: ${convertToVwMob(15)};
  width: ${({ $stepCount = 4 }) => convertToVwMob($stepCount === 3 ? 128 : 193)};
  height: 1px;
  background-color: #e5e5e5;
  z-index: 0;
  transform: translateY(-50%);
`;

const StepCircle = styled.div<{ active?: boolean }>`
  width: ${props => (props.active ? convertToVwMob(30) : convertToVwMob(20))};
  height: ${props => (props.active ? convertToVwMob(30) : convertToVwMob(20))};
  border-radius: 50%;
  background-color: ${props => (props.active ? theme.colors.primaryBlue[40] : '#e5e5e5')};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  color: white;
  margin-right: ${convertToVwMob(44)};
  
  &:last-child {
    margin-right: 0;
  }

  p {
    ${theme.typography.contents.regular20};
    font-size: ${props => (props.active ? convertToVwMob(20) : convertToVwMob(12))};
    margin: 0;
    line-height: 1;
  }
`;

const TabMenu = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
`;

const TabRow = styled.div`
  display: flex;
  width: 100%;
`;

const TabItem = styled.div<{
  active?: boolean;
  borderLeft?: boolean;
  borderBottom?: boolean;
  borderRight?: boolean;
  borderTop?: boolean;
  topLeftRadius?: boolean;
  topRightRadius?: boolean;
  $compact?: boolean;
  /** 영어 탭: 왼쪽 정렬 + flex 너비 고정(50% 셀 안에서 줄바꿈) */
  $enAlignStart?: boolean;
}>`
  box-sizing: border-box;
  flex: 1;
  min-width: ${props => (props.$enAlignStart ? 0 : 'auto')};
  display: flex;
  align-items: center;
  justify-content: ${props => (props.$enAlignStart ? 'flex-start' : 'center')};
  gap: ${props => convertToVwMob(props.$compact ? 6 : 10)};
  cursor: pointer;
  padding: ${props => `${convertToVwMob(12)} ${convertToVwMob(props.$compact ? 8 : 20)}`};
  background-color: ${props => (props.active ? theme.colors.primaryBlue[10] : 'white')};
  border-bottom: ${props => (props.active ? `4px solid ${theme.colors.primaryBlue[40]}` : props.borderBottom ? `1px solid ${theme.colors.grayScale[20]}` : 'none')};
  border-left: ${props => (props.borderLeft ? `1px solid ${theme.colors.grayScale[20]}` : 'none')};
  border-right: ${props => (props.borderRight ? `1px solid ${theme.colors.grayScale[20]}` : 'none')};
  border-top: ${props => (props.borderTop ? `1px solid ${theme.colors.grayScale[20]}` : 'none')};
  border-top-left-radius: ${props => (props.topLeftRadius ? convertToVwMob(10) : '0')};
  border-top-right-radius: ${props => (props.topRightRadius ? convertToVwMob(10) : '0')};

  img {
    width: ${props => convertToVwMob(props.$compact ? 24 : 32)};
    height: ${props => convertToVwMob(props.$compact ? 24 : 32)};
    min-width: ${props => convertToVwMob(props.$compact ? 24 : 32)};
    min-height: ${props => convertToVwMob(props.$compact ? 24 : 32)};
    object-fit: contain;
    flex-shrink: 0;
  }

  p {
    ${theme.typography.contents.medium20};
    font-size: ${props => (props.$compact ? convertToVwMob(16) : convertToVwMob(20))};
    line-height: 1.2;
    color: ${props => (props.active ? theme.colors.primaryBlue[40] : theme.colors.grayScale[70])};
    margin: 0;
    ${props =>
      props.$enAlignStart
        ? `
      min-width: 0;
      flex: 1;
      text-align: left;
    `
        : ''}
  }
`;

const TabTextRow = styled.span<{ $fillCell?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${convertToVwMob(6)};
  min-width: 0;
  ${props =>
    props.$fillCell &&
    `
    flex: 1;
  `}
`;

const MobTabSettlementWarnIcon = styled.img`
  width: ${convertToVwMob(16)} !important;
  height: ${convertToVwMob(16)} !important;
  min-width: ${convertToVwMob(16)} !important;
  min-height: ${convertToVwMob(16)} !important;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwMob(32)};
  width: 100%;
`;

const ProfileSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwMob(16)};
`;

const ProfileImageWrapper = styled.div<{ status?: string }>`
  width: ${convertToVwMob(126)};
  height: ${convertToVwMob(126)};
  border-radius: ${convertToVwMob(48)};
  position: relative;
  background: ${({ status }) => {
    if (status === 'in-progress') return 'linear-gradient(135deg, #B9F3FF 0%, #FFFFFF 100%)';
    if (status === 'complete') return 'linear-gradient(135deg, #2BC2E1 0%, #FFFFFF 100%)';
    return '#f0f1f2';
  }};
  padding: ${({ status }) => (status === 'in-progress' || status === 'complete' ? '5px' : '0')};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProfileImage = styled.img<{ $contain?: boolean }>`
  width: 99%;
  height: 99%;
  border-radius: calc(${convertToVwMob(48)} - 4px);
  object-fit: ${({ $contain }) => ($contain ? 'contain' : 'cover')};
  background-color: white;
  z-index: 1;
`;

const CameraIcon = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: ${convertToVwMob(40)};
  height: ${convertToVwMob(40)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ProfileName = styled.p`
  ${theme.typography.head.semiBold20};
  color: ${theme.colors.grayScale[90]};
  margin: 0;
`;

const InputList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(32)};
  width: 100%;
`;

const InputBox = styled.div<{ disabled?: boolean; error?: boolean; active?: boolean }>`
  background-color: ${props => (props.disabled ? theme.colors.grayScale[10] : 'white')};
  border: ${props => (props.error ? '1.6px solid #ff0000' : props.active ? `1.6px solid ${theme.colors.primaryBlue[40]}` : `1px solid ${theme.colors.grayScale[20]}`)};
  border-radius: ${convertToVwMob(4)};
  padding: ${convertToVwMob(8)} ${convertToVwMob(12)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(2)};
  position: relative;
  width: 100%;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
  width: 100%;
`;

const UnderlineInputWrapper = styled.div<{ $error?: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(8)};
  border-bottom: 1px solid ${({ $error }) => ($error ? '#ff0000' : '#BCBCBC')};
  padding-bottom: ${convertToVwMob(8)};
  margin-top: ${convertToVwMob(8)};

  &:focus-within {
    border-bottom: 1px solid ${({ $error, theme }) => ($error ? '#ff0000' : theme.colors.primaryBlue[40])};
  }
`;

const UnderlineInput = styled.input`
  flex: 1;
  padding: 0;
  border: none;
  background: transparent;
  outline: none;
  ${theme.typography.contents.regular24};
  color: ${theme.colors.grayScale[90]};

  &::placeholder {
    color: ${theme.colors.grayScale[70]};
  }
`;

const UnderlineClearBtn = styled.button`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
`;

const InputLabel = styled.label`
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[60]};
`;

const InputValue = styled.p<{ placeholder?: boolean; error?: boolean; disabled?: boolean }>`
  ${theme.typography.contents.regular24};
  color: ${props => (props.placeholder || props.disabled ? theme.colors.grayScale[50] : props.error ? '#ff0000' : theme.colors.grayScale[90])};
  margin: 0;
`;

const StyledInput = styled.input<{ disabled?: boolean; error?: boolean }>`
  ${theme.typography.contents.regular24};
  color: ${props => (props.disabled ? theme.colors.grayScale[50] : props.error ? '#ff0000' : theme.colors.grayScale[90])};
  border: none;
  background: transparent;
  outline: none;
  padding: 0;
  width: 100%;

  &::placeholder {
    color: ${theme.colors.grayScale[50]};
  }
`;

const BottomSheetScrollable = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  /* 취소/다음 버튼에 가리지 않도록 하단 여백 */
  padding-bottom: ${convertToVwMob(80)};
`;

const BottomSheetItem = styled.div<{ active?: boolean }>`
  padding: ${convertToVwMob(16)} ${convertToVwMob(8)};
  border-bottom: 1px solid ${theme.colors.grayScale[10]};
  ${theme.typography.contents.regular20};
  color: ${props => (props.active ? theme.colors.primaryBlue[40] : theme.colors.grayScale[90])};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:last-child {
    border-bottom: none;
  }
`;

const DropdownGroupLabel = styled.div`
  padding: ${convertToVwMob(8)} ${convertToVwMob(12)};
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[60]};
  cursor: default;
`;

const ErrorText = styled.p`
  ${theme.typography.contents.regular20};
  color: #ff0000;
  margin: ${convertToVwMob(4)} 0 0 0;
`;

const IconWrapper = styled.div<{ rotate?: boolean }>`
  position: absolute;
  right: ${convertToVwMob(12)};
  top: 50%;
  transform: translateY(-50%) ${props => (props.rotate ? 'rotate(180deg)' : 'rotate(0deg)')};
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  transition: transform 0.2s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: ${convertToVwMob(35)};
  align-items: center;
`;

const RadioItem = styled.div`
  display: flex;
  gap: ${convertToVwMob(10)};
  align-items: center;
  cursor: pointer;
`;

const RadioCircle = styled.div<{ active?: boolean }>`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  border-radius: 50%;
  border: 1.5px solid #E5E5E5;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex: 0 0 auto;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${convertToVwMob(12)};
    height: ${convertToVwMob(12)};
    border-radius: 50%;
    background: ${theme.colors.primaryBlue[40]};
    transform: ${({ active }) =>
      active ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0)'};
    transition: transform 0.15s ease;
  }
`;

const RadioLabel = styled.span`
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[90]};
  min-width: 0;
  overflow-wrap: anywhere;
`;

const CheckboxGroup = styled.div`
  display: flex;
  gap: ${convertToVwMob(12)};
  align-items: center;
  cursor: pointer;
  min-width: 0;
`;

const CheckboxSquare = styled.div<{ active?: boolean; checkColor?: string }>`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  flex: 0 0 ${convertToVwMob(20)};
  border: 1px solid ${props => (props.active ? theme.colors.primaryBlue[40] : theme.colors.grayScale[40])};
  border-radius: ${convertToVwMob(2)};
  background-color: ${props => (props.active ? theme.colors.primaryBlue[40] : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;

  ${props =>
    props.active &&
    `
    &::after {
      content: '✓';
      color: ${props.checkColor || 'white'};
      font-size: ${convertToVwMob(14)};
    }
  `}
`;

const UploadedFileRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${convertToVwMob(8)};
  width: 100%;
  align-items: stretch;
`;

const UploadedFileBox = styled.div`
  background-color: white;
  border: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-width: 0;
  padding: ${convertToVwMob(12)} ${convertToVwMob(16)};
  border-radius: ${convertToVwMob(12)};
  box-sizing: border-box;
`;

const UploadedFileLeft = styled.div`
  display: flex;
  gap: ${convertToVwMob(8)};
  align-items: center;
  min-width: 0;
  flex: 1;
`;

const UploadedFileName = styled.p`
  color: #404040;
  font-size: ${convertToVwMob(16)};
  line-height: 150%;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UploadedFileSize = styled.p`
  color: #676767;
  font-size: ${convertToVwMob(16)};
  line-height: 150%;
  margin: 0;
  flex-shrink: 0;
`;

const FileDeleteButton = styled.button`
  height: 100%;
  aspect-ratio: 1;
  width: auto;
  min-width: 0;
  background-color: #eee;
  border: 1px solid #eee;
  border-radius: ${convertToVwMob(12)};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  box-sizing: border-box;
`;

const getFileName = (fileUrl: string | null, meta: {name: string, size: string} | undefined) => {
  if (meta) return meta.name;
  if (!fileUrl) return '';
  if (fileUrl.startsWith('data:')) return '업로드된 파일';
  return fileUrl.split('/').pop() || '업로드된 파일';
};

const UploadIconSvg = ({ disabled }: { disabled?: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none">
    <path d="M11 16V7.85L8.4 10.45L7 9L12 4L17 9L15.6 10.45L13 7.85V16H11ZM6 20C5.45 20 4.97933 19.8043 4.588 19.413C4.19667 19.0217 4.00067 18.5507 4 18V15H6V18H18V15H20V18C20 18.55 19.8043 19.021 19.413 19.413C19.0217 19.805 18.5507 20.0007 18 20H6Z" fill={disabled ? '#BCBCBC' : '#17171B'}/>
  </svg>
);

const FileItemIconSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none">
    <path d="M13.172 3H9C7.114 3 6.172 3 5.586 3.586C5 4.172 5 5.114 5 7V17C5 18.886 5 19.828 5.586 20.414C6.172 21 7.114 21 9 21H15C16.886 21 17.828 21 18.414 20.414C19 19.828 19 18.886 19 17V8.828C19 8.42 19 8.215 18.924 8.032C18.848 7.849 18.704 7.703 18.414 7.414L14.586 3.586C14.296 3.296 14.152 3.152 13.969 3.076C13.785 3 13.58 3 13.172 3Z" stroke="#BCBCBC" strokeWidth="2"/>
    <path d="M13 3V7C13 7.943 13 8.414 13.293 8.707C13.586 9 14.057 9 15 9H19" stroke="#BCBCBC" strokeWidth="2"/>
  </svg>
);

const FileDeleteIconSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none">
    <path d="M12.8986 11.9962L19.6522 5.23595C19.7713 5.11621 19.8382 4.95414 19.838 4.78523C19.8379 4.61631 19.7709 4.45433 19.6516 4.33475C19.4128 4.09715 18.9922 4.09595 18.751 4.33595L11.9992 11.0961L5.24497 4.33415C5.00497 4.09715 4.58437 4.09835 4.34557 4.33535C4.2863 4.39439 4.23938 4.46464 4.20754 4.542C4.17571 4.61937 4.15961 4.70229 4.16017 4.78595C4.16017 4.95635 4.22617 5.11595 4.34557 5.23415L11.0992 11.9956L4.34617 18.7576C4.227 18.8775 4.16027 19.0398 4.1606 19.2088C4.16094 19.3779 4.22832 19.5399 4.34797 19.6594C4.46377 19.7739 4.62757 19.84 4.79677 19.84H4.80037C4.97017 19.8394 5.13397 19.7728 5.24737 19.657L11.9992 12.8968L18.7534 19.6588C18.8728 19.7776 19.0324 19.8436 19.2016 19.8436C19.2852 19.8436 19.368 19.8272 19.4453 19.7953C19.5226 19.7633 19.5928 19.7164 19.6519 19.6573C19.711 19.5981 19.7579 19.5279 19.7899 19.4507C19.8218 19.3734 19.8382 19.2906 19.8382 19.207C19.8382 19.0372 19.7722 18.877 19.6522 18.7587L12.8986 11.9962Z" fill="black"/>
  </svg>
);

const UploadBox = styled.div<{ error?: boolean; disabled?: boolean; filled?: boolean }>`
  width: 100%;
  padding: ${convertToVwMob(32)} 0;
  border: 1px dashed ${props =>
    props.disabled ? '#EEE' : props.error ? '#ff0000' : '#EEE'};
  border-radius: ${convertToVwMob(12)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(12)};
  position: relative;
  overflow: hidden;
  background-color: ${({ disabled }) => (disabled ? '#EEE' : '#FDFDFD')};
  cursor: ${({ disabled, filled }) => (disabled ? 'not-allowed' : filled ? 'default' : 'pointer')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  transition: background-color 0.2s ease, border-color 0.2s ease;

  & * {
    cursor: inherit;
  }

  &:active {
    background-color: ${({ disabled, filled }) => (disabled || filled ? undefined : '#EBFCFF')};
    border-color: ${({ disabled, filled }) => (disabled || filled ? undefined : '#2BC2E1')};
  }
`;

const UploadIcon = styled.div`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const UploadText = styled.p<{ $disabled?: boolean }>`
  ${theme.typography.contents.regular20};
  color: ${({ $disabled }) => ($disabled ? '#BCBCBC' : theme.colors.grayScale[90])};
  margin: 0;
  text-align: center;
  white-space: pre-wrap;
  word-break: keep-all;
`;

const UploadSubText = styled.p<{ $disabled?: boolean }>`
  ${theme.typography.contents.regular16};
  color: ${({ $disabled }) => ($disabled ? '#BCBCBC' : theme.colors.grayScale[70])};
  margin: 0;
  text-align: center;
`;

const UploadContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(12)};
  align-items: center;
`;

const UploadContentRow = styled.div`
  display: flex;
  gap: ${convertToVwMob(8)};
  align-items: center;
  justify-content: center;
`;

const UploadContentRowLong = styled.div`
  display: flex;
  gap: ${convertToVwMob(16)};
  align-items: center;
  justify-content: center;
`;

const UploadIconGroup = styled.div`
  display: flex;
  gap: ${convertToVwMob(4)};
  align-items: center;
  flex-shrink: 0;
`;

const UploadIconGroupVertical = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(8)};
  align-items: center;
  flex-shrink: 0;
`;

const UploadBoxWarningIcon = styled(MobTabSettlementWarnIcon)`
  width: ${convertToVwMob(20)} !important;
  height: ${convertToVwMob(20)} !important;
  min-width: ${convertToVwMob(20)} !important;
  min-height: ${convertToVwMob(20)} !important;
`;

const UploadEmptyState: React.FC<{
  text: string;
  subText: string;
  showWarning?: boolean;
  layout?: 'short' | 'long';
  $disabled?: boolean;
}> = ({ text, subText, showWarning, layout = 'short', $disabled }) => {
  const iconGroup = (
    <>
      <UploadIcon>
        <UploadIconSvg disabled={$disabled} />
      </UploadIcon>
      {showWarning ? <UploadBoxWarningIcon src={noYellowIcon} alt="" aria-hidden /> : null}
    </>
  );

  return (
    <UploadContent>
      {layout === 'long' ? (
        <UploadContentRowLong>
          <UploadIconGroupVertical>{iconGroup}</UploadIconGroupVertical>
          <UploadText $disabled={$disabled}>{text}</UploadText>
        </UploadContentRowLong>
      ) : (
        <UploadContentRow>
          <UploadIconGroup>{iconGroup}</UploadIconGroup>
          <UploadText $disabled={$disabled}>{text}</UploadText>
        </UploadContentRow>
      )}
      <UploadSubText $disabled={$disabled}>{subText}</UploadSubText>
    </UploadContent>
  );
};

const UploadButton = styled.div<{ $disabled?: boolean }>`
  width: ${convertToVwMob(200)};
  padding: ${convertToVwMob(10)} ${convertToVwMob(12)};
  background-color: ${({ $disabled }) => ($disabled ? '#BCBCBC' : theme.colors.primaryBlue[80])};
  color: white;
  border-radius: ${convertToVwMob(5)};
  ${theme.typography.contents.medium20};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  flex-wrap: nowrap;
  box-sizing: border-box;
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;

const BankbookUploadedPanel = styled.div`
  width: 100%;
  box-sizing: border-box;
  border-radius: ${convertToVwMob(12)};
  background: var(--PrimaryBlue-5, #f8fdff);
  min-height: ${convertToVwMob(256)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${convertToVwMob(28)} ${convertToVwMob(24)};
  gap: ${convertToVwMob(16)};
`;

const BankbookUploadedMessage = styled.p`
  margin: 0;
  color: var(--PrimaryBlue-80, #026478);
  font-family: Pretendard, sans-serif;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: ${convertToVwMob(28)};
  text-align: center;
`;

function BankbookUploadedCheckSvg({ size }: { size: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 75 75" fill="none" style={{ width: size, height: size, display: 'block' }} aria-hidden>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M37.5 75C42.4246 75 47.3009 74.03 51.8506 72.1455C56.4003 70.2609 60.5343 67.4987 64.0165 64.0165C67.4987 60.5343 70.2609 56.4003 72.1455 51.8506C74.03 47.3009 75 42.4246 75 37.5C75 32.5754 74.03 27.6991 72.1455 23.1494C70.2609 18.5997 67.4987 14.4657 64.0165 10.9835C60.5343 7.5013 56.4003 4.73907 51.8506 2.85452C47.3009 0.969966 42.4246 -7.33818e-08 37.5 0C27.5544 1.48201e-07 18.0161 3.95088 10.9835 10.9835C3.95088 18.0161 0 27.5544 0 37.5C0 47.4456 3.95088 56.9839 10.9835 64.0165C18.0161 71.0491 27.5544 75 37.5 75ZM36.5333 52.6667L57.3667 27.6667L50.9667 22.3333L33.05 43.8292L23.7792 34.5542L17.8875 40.4458L30.3875 52.9458L33.6125 56.1708L36.5333 52.6667Z"
        fill="#026478"
      />
    </svg>
  );
}

const PreviewOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(16)};
  z-index: 2;
`;

const ImagePreviewOverlay = styled(PreviewOverlay)<{ $imageUrl: string }>`
  background: ${({ $imageUrl }) =>
    `linear-gradient(0deg, rgba(0, 0, 0, 0.30) 0%, rgba(0, 0, 0, 0.30) 100%), url(${$imageUrl}) lightgray 50% / cover no-repeat`};
`;

const PreviewImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

const PreviewDimLayer = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1;
  pointer-events: none;
`;

const TipBox = styled.div`
  background-color: ${theme.colors.yellow[5]};
  border-radius: ${convertToVwMob(20)};
  padding: ${convertToVwMob(24)} ${convertToVwMob(20)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(16)};
  width: 100%;
`;

const TipHeader = styled.div`
  display: flex;
  gap: ${convertToVwMob(12)};
  align-items: flex-start;
`;

const TipIcon = styled.div`
  width: ${convertToVwMob(34)};
  height: ${convertToVwMob(34)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const TipText = styled.p`
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[90]};
  margin: 0;
  flex: 1;
  line-height: 1.5;
`;

const BottomButtonGroup = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  border-top: 0.5px solid ${theme.colors.grayScale[40]};
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: ${convertToVwMob(16)} ${convertToVwMob(32)};
  z-index: 1100;
  padding-bottom: calc(${convertToVwMob(32)} + env(safe-area-inset-bottom));
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${convertToVwMob(16)};
  align-items: center;
`;

const Button = styled.button<{ primary?: boolean; $loading?: boolean }>`
  width: ${convertToVwMob(130)};
  height: ${convertToVwMob(54)};
  padding: ${convertToVwMob(10)};
  flex-shrink: 0;
  border-radius: ${convertToVwMob(5)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(10)};
  cursor: pointer;
  ${theme.typography.button.regular20};
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;

  ${props => props.primary ? `
    background-color: ${props.$loading ? '#EBFCFF' : theme.colors.primaryBlue[80]};
    color: ${props.$loading ? '#2BC2E1' : 'white'};
    border: none;
  ` : `
    background-color: white;
    color: #676767;
    border: 1px solid #DFDFDF;
  `}

  &:disabled {
    cursor: not-allowed;
    ${props => props.primary ? `
      background-color: ${props.$loading ? '#EBFCFF' : '#DFDFDF'};
      color: ${props.$loading ? '#2BC2E1' : '#BCBCBC'};
    ` : `
      background-color: #fff;
      color: #BCBCBC;
      border: 1px solid #DFDFDF;
    `}
  }
`;

const ButtonLoadingIcon = styled(DotLottieReact)`
  display: inline-flex;
`;

const VerificationBadge = styled.div<{ status: 'in-progress' | 'complete' }>`
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(4)};
  padding: ${convertToVwMob(4)} ${convertToVwMob(12)};
  border-radius: ${convertToVwMob(16)};
  background-color: ${props => props.status === 'in-progress' ? '#DEF9FF' : theme.colors.primaryBlue[40]};

  img {
    width: ${convertToVwMob(20)};
    height: ${convertToVwMob(20)};
    object-fit: contain;
  }

  p {
    ${theme.typography.chip.semiBold16};
    color: ${props => props.status === 'in-progress' ? theme.colors.primaryBlue[40] : 'white'};
    margin: 0;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContainer = styled.div`
  background-color: white;
  padding: ${convertToVwMob(38)} ${convertToVwMob(28)} ${convertToVwMob(26)};
  border-radius: ${convertToVwMob(20)};
  width: ${convertToVwMob(360)};
  max-width: calc(100vw - ${convertToVwMob(32)});
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwMob(24)};
  box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.35);
`;

const ModalTitle = styled.p`
  ${theme.typography.contents.medium20};
  font-size: ${convertToVwMob(24)};
  color: ${theme.colors.grayScale[90]};
  margin: 0;
  text-align: center;
`;

const ModalDesc = styled.div`
  ${theme.typography.contents.regular16};
  font-size: ${convertToVwMob(20)};
  line-height: 1.5;
  color: ${theme.colors.grayScale[90]};
  text-align: center;
  p {
    margin: 0;
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: ${convertToVwMob(12)};
  width: 100%;
`;

const ModalButton = styled.button<{ primary?: boolean }>`
  flex: ${props => props.primary ? 2 : 1};
  height: ${convertToVwMob(58)};
  border-radius: ${convertToVwMob(12)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  ${theme.typography.contents.regular20};
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  
  ${props => props.primary ? `
    background-color: ${theme.colors.primaryBlue[80]};
    color: white;
    border: none;
  ` : `
    background-color: ${theme.colors.grayScale[40]};
    color: ${theme.colors.grayScale[90]};
    border: none;
  `}
`;

// --- Components ---

const MyAccountMob = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('translation');
  /** 영어 탭만 라벨 왼쪽 정렬·셀 너비 유지(한국어는 기존 가운데 정렬 유지) */
  const isEnMyAccountTabs = /^en/i.test(i18n.resolvedLanguage || i18n.language || '');
  // 인증된 사용자 ID 가져오기
  const authUser = useAppSelector(selectAuthUser);
  const userId = authUser?.user_id; // || '240F3A70'; // 테스트용 기본값

  // useMyAccount hook에서 모든 상태와 핸들러 가져오기
  const {
    isLoading,
    isSaving,
    userProfile,
    activeTab,
    setActiveTab,
    profileImage,
    setProfileImage,
    userType,
    organization,
    setOrganization,
    organizationOther,
    setOrganizationOther,
    fullName,
    contactName,
    setContactName,
    birthDate,
    setBirthDate,
    isBirthDateLocked,
    isBankInfoLocked,
    isVerificationLocked,
    showBankTabSettlementWarning,
    showVerificationTabSettlementWarning,
    phone,
    email,
    country,
    setCountry,
    language,
    setLanguage,
    certificateFile,
    setCertificateFile,
    ministryCountry,
    setMinistryCountry,
    isSecurityCountry,
    setIsSecurityCountry,
    bankCountry,
    setBankCountry,
    bankName,
    setBankName,
    accountNumber,
    setAccountNumber,
    accountHolder,
    setAccountHolder,
    bankbookFile,
    setBankbookFile,
    usTransferMethod,
    setUsTransferMethod,
    streetAddress,
    setStreetAddress,
    city,
    setCity,
    state,
    setState,
    zipCode,
    setZipCode,
    zelleContact,
    setZelleContact,
    hasNoUniqueId,
    setHasNoUniqueId,
    uniqueId,
    setUniqueId,
    residentRegistrationNumber,
    setResidentRegistrationNumber,
    idFile,
    setIdFile,
    verificationStatus,
    setVerificationStatus,
    errors,
    setErrors,
    showErrors,
    isSubmitModalOpen,
    setIsSubmitModalOpen,
    isSettlementDocsCompleteModalOpen,
    setIsSettlementDocsCompleteModalOpen,
    handleSave,
    handleCancel,
    handleNext,
    handlePrev,
    handleClearAll,
    isDirty,
  } = useMyAccount(userId);

  const isStepMode = verificationStatus === 'none';
  /** 최초 스텝 입력 중에는 파일선택 옆 경고 아이콘 숨김 — 탭 모드(보완 업로드)에서만 표시 */
  const showMissingDocIconOnFileSelect = !isStepMode;
  const shouldHideVerificationTab = bankCountry === 'US';

  useEffect(() => {
    if (shouldHideVerificationTab && activeTab === 'verification') {
      setActiveTab('basic');
    }
  }, [activeTab, setActiveTab, shouldHideVerificationTab]);

  React.useEffect(() => {
    if (!isStepMode) return;
    document.querySelector<HTMLElement>('[data-mobile-scroll-main]')?.scrollTo({ top: 0, behavior: 'auto' });
  }, [activeTab, isStepMode]);

  // 드롭다운 상태 (로컬에서만 관리)
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = React.useState(false);
  const [isOrganizationDropdownOpen, setIsOrganizationDropdownOpen] = React.useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = React.useState(false);
  const [isBankDropdownOpen, setIsBankDropdownOpen] = React.useState(false);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = React.useState(false);
  const [isMinistryCountryDropdownOpen, setIsMinistryCountryDropdownOpen] = React.useState(false);
  const [isBirthDatePickerOpen, setIsBirthDatePickerOpen] = React.useState(false);
  const [profileImageError, setProfileImageError] = React.useState(false);

  React.useEffect(() => {
    setProfileImageError(false);
  }, [profileImage]);
  useEffect(() => {
    if (isBirthDateLocked) setIsBirthDatePickerOpen(false);
  }, [isBirthDateLocked]);
  useEffect(() => {
    if (isBankInfoLocked) {
      setIsBankDropdownOpen(false);
      setIsStateDropdownOpen(false);
    }
  }, [isBankInfoLocked]);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const pendingCancelHandlerRef = useRef<(() => void) | null>(null);

  const handleRequestCancel = () => {
    setIsCancelConfirmOpen(true);
  };

  const handleConfirmCancel = () => {
    pendingCancelHandlerRef.current?.();
    pendingCancelHandlerRef.current = null;
    setIsCancelConfirmOpen(false);
  };

  const countryOptions = getCountryOptions();
  const organizationOptions = React.useMemo(
    () => getOrganizationOptions(),
    [i18n.language, i18n.resolvedLanguage],
  );
  const organizationDisplayName = getOrganizationDisplayName(organization);
  const profileOrgSuffix = organization === ORGANIZATION_ETC_CODE && organizationOther.trim()
    ? organizationOther.trim()
    : organizationDisplayName;
  const languages = [
    t('common.languages.ko'),
    t('common.languages.en'),
  ];
  const bankGroups = React.useMemo(
    () =>
      (t('page.myAccount.bankGroupsData', {
        returnObjects: true,
        defaultValue: [],
      }) as unknown as Array<{ type: string; label: string; options: string[] }>),
    [t],
  );
  const states = US_STATES;

  const [fileMeta, setFileMeta] = useState<Record<string, {name: string, size: string}>>({});

  const triggerFileUpload = (inputId: string, hasExistingFile: boolean) => {
    if (hasExistingFile) return;
    document.getElementById(inputId)?.click();
  };

  const resetFileInput = (inputId: string) => {
    const input = document.getElementById(inputId) as HTMLInputElement | null;
    if (input) input.value = '';
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (value: string | null) => void,
    errorKey?: string,
    fileKey?: string,
    options?: { validateCertificate?: boolean },
  ) => {
    const file = e.target.files?.[0];
    if (!file) {
      e.target.value = '';
      return;
    }

    if (options?.validateCertificate) {
      if (!isCertificateImageFile(file)) {
        alert(t('common.errors.certificateInvalidType'));
        e.target.value = '';
        return;
      }
      if (!isImageSizeAllowed(file, MAX_CERTIFICATE_UPLOAD_BYTES)) {
        alert(t('common.errors.certificateTooLarge'));
        e.target.value = '';
        return;
      }
    }

    if (fileKey) {
      setFileMeta(prev => ({ ...prev, [fileKey]: { name: file.name, size: (file.size / 1024 / 1024).toFixed(2) + ' MB' } }));
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setter(reader.result as string);
      if (errorKey && errors[errorKey]) {
        setErrors({ ...errors, [errorKey]: false });
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // 로딩 상태 표시
  if (isLoading) {
    return (
      <MobilePageLayout title={t('page.myAccount.pageTitle')} showBottomNav={false}>
          <DSLoading />
      </MobilePageLayout>
    );
  }

  const renderStepIndicator = () => {
    const steps = bankCountry === 'US' ? [1, 2, 3] : [1, 2, 3, 4];
    const currentStep = activeTab === 'basic' ? 1 : activeTab === 'ministry' ? 2 : activeTab === 'bank' ? 3 : 4;

    const stepListEl = (
        <StepList>
          <StepLine $stepCount={steps.length} />
          {steps.map(step => (
            <StepCircle key={step} active={step === currentStep}>
              <p>{step}</p>
            </StepCircle>
          ))}
        </StepList>
    );

    return (
      <StepIndicatorWrapper>
        <StepHintStack>
          <StepHintBlackBlock>
            <StepText>{t('page.myAccount.stepHintMobile')}</StepText>
            <StepText>{t('page.myAccount.stepHintLine2')}</StepText>
          </StepHintBlackBlock>
          <StepSubText>{t(`page.myAccount.stepSubHint.${activeTab}`)}</StepSubText>
          <StepProgressRow>{stepListEl}</StepProgressRow>
        </StepHintStack>
      </StepIndicatorWrapper>
    );
  };

  const renderBasicInfo = () => (
    (() => {
      const isIndividual = isIndividualUserType(userProfile?.user_type);
      const nameLabel = isIndividual ? t('page.myAccount.labels.name') : t('page.myAccount.labels.contactName');
      const birthLabel = isIndividual ? t('page.myAccount.labels.birthDate') : t('page.myAccount.labels.contactBirthDate');

      return (
    <>
      <ProfileSection>
        <ProfileImageWrapper status={verificationStatus}>
          <input
            type="file"
            id="profile-upload-mob"
            style={{ display: 'none' }}
            accept="image/*"
            onChange={(e) => handleFileChange(e, setProfileImage)}
          />
          {profileImage?.trim() && !profileImageError ? (
            <ProfileImage src={profileImage} alt="" onError={() => setProfileImageError(true)} />
          ) : (
            <ProfileImage src={defaultProfileIcon} alt={t('page.myAccount.profile.defaultAlt')} $contain />
          )}
          <CameraIcon onClick={() => document.getElementById('profile-upload-mob')?.click()}>
            <img src={photoIcon} alt="camera" />
          </CameraIcon>
        </ProfileImageWrapper>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: convertToVwMob(4) }}>
          <ProfileName>
            {`${isIndividual ? fullName : contactName}${profileOrgSuffix ? ` (${profileOrgSuffix})` : ''}`}
          </ProfileName>
          {verificationStatus === 'in-progress' && (
            <VerificationBadge status="in-progress">
              <img src={profileCertificateIcon} alt="certificate" />
              <p>{t('page.myAccount.verification.inProgress')}</p>
            </VerificationBadge>
          )}
          {verificationStatus === 'complete' && (
            <VerificationBadge status="complete">
              <img src={profileCertificateFilledIcon} alt="certificate-filled" />
              <p>{t('page.myAccount.verification.complete')}</p>
            </VerificationBadge>
          )}
        </div>
      </ProfileSection>
      <InputList>
        <InputBox disabled>
          <InputLabel>{t('page.myAccount.labels.userType')}</InputLabel>
          <InputValue disabled>{userType || t('page.myAccount.placeholders.userType')}</InputValue>
        </InputBox>
        
        {/* Organization: 선교사/선교단체 사역자 → 드롭다운(소속 기관명), 교회 사역자/개인 기타/단체 → 텍스트(소속 단체명) */}
        <InputGroup>
          {isIndividual && isOrganizationDropdownForIndividual(userProfile?.user_type) ? (
            <>
              <InputBox active={isOrganizationDropdownOpen} error={showErrors && errors.organization} onClick={() => setIsOrganizationDropdownOpen(true)}>
                <InputLabel>{t('page.myAccount.labels.organizationInstitution')}</InputLabel>
                <InputValue placeholder={!organization}>{getOrganizationDisplayName(organization) || t('page.myAccount.placeholders.organization')}</InputValue>
                <IconWrapper rotate={isOrganizationDropdownOpen}>
                  <img src={dropdownIcon} alt="dropdown" />
                </IconWrapper>
              </InputBox>
              {showErrors && errors.organization && <ErrorText>{t('common.errors.required')}</ErrorText>}
              {organization === ORGANIZATION_ETC_CODE && (
                <InputGroup>
                  <UnderlineInputWrapper $error={showErrors && errors.organizationOther}>
                    <UnderlineInput
                      placeholder={t('page.myAccount.labels.organizationOther')}
                      value={organizationOther}
                      onChange={(e) => {
                        setOrganizationOther(e.target.value);
                        if (errors.organizationOther) setErrors({ ...errors, organizationOther: false });
                      }}
                    />
                    {organizationOther && (
                      <UnderlineClearBtn type="button" onClick={() => setOrganizationOther('')}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" fill="#BCBCBC" />
                          <path d="M8 8L16 16M16 8L8 16" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </UnderlineClearBtn>
                    )}
                  </UnderlineInputWrapper>
                  {showErrors && errors.organizationOther && <ErrorText>{t('common.errors.required')}</ErrorText>}
                </InputGroup>
              )}
            </>
          ) : (
            <>
              <InputBox error={showErrors && errors.organization}>
                {organization && <InputLabel>{t('page.myAccount.labels.organization')}</InputLabel>}
                <StyledInput
                  placeholder={t('page.myAccount.placeholders.organization')}
                  value={organization}
                  onChange={(e) => {
                    setOrganization(e.target.value);
                    if (errors.organization) setErrors({ ...errors, organization: false });
                  }}
                />
                {organization && (
                  <IconWrapper onClick={() => setOrganization('')}>
                    <img src={deleteIcon} alt="clear" />
                  </IconWrapper>
                )}
              </InputBox>
              {showErrors && errors.organization && <ErrorText>{t('common.errors.required')}</ErrorText>}
            </>
          )}
        </InputGroup>
        {isIndividual ? (
        <InputBox disabled>
            <InputLabel>{nameLabel}</InputLabel>
          <InputValue disabled>{fullName || t('page.myAccount.placeholders.name')}</InputValue>
        </InputBox>
        ) : (
          <InputGroup>
            <InputBox error={showErrors && errors.contactName}>
              {contactName && <InputLabel>{nameLabel}</InputLabel>}
              <StyledInput
                placeholder={nameLabel}
                value={contactName}
                onChange={(e) => {
                  setContactName(e.target.value);
                  if (errors.contactName) setErrors({ ...errors, contactName: false });
                }}
              />
              {contactName && (
                <IconWrapper onClick={() => setContactName('')}>
                  <img src={deleteIcon} alt="clear" />
                </IconWrapper>
              )}
            </InputBox>
            {showErrors && errors.contactName && <ErrorText>{t('common.errors.required')}</ErrorText>}
          </InputGroup>
        )}
        <InputGroup>
          <InputBox
            disabled={isBirthDateLocked}
            error={!isBirthDateLocked && showErrors && errors.birthDate}
            onClick={isBirthDateLocked ? undefined : () => setIsBirthDatePickerOpen(true)}
            style={{ cursor: isBirthDateLocked ? 'not-allowed' : 'pointer' }}
          >
            <InputLabel>{birthLabel}</InputLabel>
            <InputValue placeholder={!birthDate} disabled={isBirthDateLocked}>
              {birthDate || birthLabel}
            </InputValue>
            {!isBirthDateLocked ? (
              <IconWrapper>
                <img src={calendarIcon} alt="date" />
              </IconWrapper>
            ) : null}
          </InputBox>
          {!isBirthDateLocked && showErrors && errors.birthDate ? (
            <ErrorText>{t('common.errors.required')}</ErrorText>
          ) : null}
        </InputGroup>
        {!isBirthDateLocked ? (
          <BirthDatePicker
            isOpen={isBirthDatePickerOpen}
            value={birthDate}
            onConfirm={(v) => {
              setBirthDate(v);
              if (errors.birthDate) setErrors({ ...errors, birthDate: false });
              setIsBirthDatePickerOpen(false);
            }}
            onCancel={() => setIsBirthDatePickerOpen(false)}
          />
        ) : null}
        <InputBox disabled>
          <InputLabel>{t('page.myAccount.labels.phone')}</InputLabel>
          <InputValue disabled>{phone || t('page.myAccount.placeholders.phone')}</InputValue>
        </InputBox>
        <InputBox disabled>
          <InputLabel>{t('page.myAccount.labels.email')}</InputLabel>
          <InputValue disabled>{email || t('page.myAccount.placeholders.email')}</InputValue>
        </InputBox>
        <InputGroup>
          <InputBox active={isCountryDropdownOpen} error={showErrors && errors.country} onClick={() => setIsCountryDropdownOpen(true)}>
            <InputLabel>{t('page.myAccount.labels.country')}</InputLabel>
            <InputValue placeholder={!country}>{getCountryLabel(country) || t('page.myAccount.placeholders.country')}</InputValue>
            <IconWrapper rotate={isCountryDropdownOpen}>
              <img src={dropdownIcon} alt="arrow" />
            </IconWrapper>
          </InputBox>
          {showErrors && errors.country && <ErrorText>{t('common.errors.required')}</ErrorText>}
        </InputGroup>
        <InputGroup>
          <InputBox active={isLanguageDropdownOpen} error={showErrors && errors.language} onClick={() => setIsLanguageDropdownOpen(true)}>
            <InputLabel>{t('page.myAccount.labels.language')}</InputLabel>
            <InputValue placeholder={!language}>{language || t('page.myAccount.placeholders.language')}</InputValue>
            <IconWrapper rotate={isLanguageDropdownOpen}>
              <img src={dropdownIcon} alt="arrow" />
            </IconWrapper>
          </InputBox>
          {showErrors && errors.language && <ErrorText>{t('common.errors.required')}</ErrorText>}
        </InputGroup>
      </InputList>
      <TipBox>
        <TipHeader>
          <TipIcon>
            <img src={hugeIcon} alt="" />
          </TipIcon>
          <TipText
            dangerouslySetInnerHTML={{
              __html: (t('page.myAccount.tips.defaultMobile') as string).replace(/\n/g, '<br />'),
            }}
          />
        </TipHeader>
      </TipBox>
    </>
      );
    })()
  );

  const renderMinistryInfo = () => (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: convertToVwMob(8) }}>
        <InputLabel style={{ color: theme.colors.grayScale[70] }}>{t('page.myAccount.labels.certificate')}</InputLabel>
        <input
          type="file"
          id="certificate-upload-mob"
          style={{ display: 'none' }}
          accept="image/jpeg,image/png,.jpg,.jpeg,.png"
          onChange={(e) => handleFileChange(e, setCertificateFile, 'hasCertificate', 'certificate', { validateCertificate: true })}
        />
        <UploadBox
          error={showErrors && errors.hasCertificate}
          filled={!!certificateFile}
          onClick={() => triggerFileUpload('certificate-upload-mob', !!certificateFile)}
        >
          <UploadEmptyState
            text={t('page.myAccount.upload.certificate')}
            subText="JPG, PNG 파일 · 최대 4MB"
            layout="short"
          />
        </UploadBox>
        {certificateFile && (
          <UploadedFileRow>
            <UploadedFileBox>
              <UploadedFileLeft>
                <div style={{ width: convertToVwMob(24), height: convertToVwMob(24), flexShrink: 0 }}>
                  <FileItemIconSvg />
                </div>
                <UploadedFileName>{getFileName(certificateFile, fileMeta['certificate'])}</UploadedFileName>
              </UploadedFileLeft>
              {fileMeta['certificate']?.size && <UploadedFileSize>{fileMeta['certificate'].size}</UploadedFileSize>}
            </UploadedFileBox>
            <FileDeleteButton
              type="button"
              onClick={() => {
                setCertificateFile(null);
                setFileMeta(prev => {
                  const newMeta = { ...prev };
                  delete newMeta['certificate'];
                  return newMeta;
                });
                resetFileInput('certificate-upload-mob');
              }}
            >
              <div style={{ width: convertToVwMob(24), height: convertToVwMob(24) }}>
                <FileDeleteIconSvg />
              </div>
            </FileDeleteButton>
          </UploadedFileRow>
        )}
        {showErrors && errors.hasCertificate && <ErrorText>{t('common.errors.required')}</ErrorText>}
      </div>
      <InputList>
        <InputGroup>
          <InputBox active={isMinistryCountryDropdownOpen} error={showErrors && errors.ministryCountry} onClick={() => setIsMinistryCountryDropdownOpen(true)}>
            <InputLabel>{t('page.myAccount.labels.ministryCountry')}</InputLabel>
            <InputValue style={{ fontSize: convertToVwMob(24) }} placeholder={!ministryCountry}>
              {getCountryLabel(ministryCountry) || t('page.myAccount.placeholders.ministryCountry')}
            </InputValue>
            <IconWrapper rotate={isMinistryCountryDropdownOpen}>
              <img src={dropdownIcon} alt="dropdown" />
            </IconWrapper>
          </InputBox>
          {showErrors && errors.ministryCountry && <ErrorText>{t('common.errors.required')}</ErrorText>}
        </InputGroup>
        <InputBox>
          <InputLabel style={{ fontSize: convertToVwMob(20) }}>{t('page.myAccount.labels.securityNote')}</InputLabel>
          <CheckboxGroup onClick={() => setIsSecurityCountry(!isSecurityCountry)}>
            <CheckboxSquare active={isSecurityCountry} />
            <RadioLabel style={{ fontSize: convertToVwMob(24) }}>{t('page.myAccount.labels.securityCountry')}</RadioLabel>
          </CheckboxGroup>
        </InputBox>
      </InputList>
      <TipBox>
        <TipHeader>
          <TipIcon>
            <img src={hugeIcon} alt="" />
          </TipIcon>
          <TipText
            dangerouslySetInnerHTML={{
              __html: (t('page.myAccount.tips.securityMobile') as string).replace(/\n/g, '<br />'),
            }}
          />
        </TipHeader>
      </TipBox>
    </>
  );

  const renderBankInfo = () => (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: convertToVwMob(12) }}>
        <p style={{ ...theme.typography.contents.medium20, margin: 0 }}>{t('page.myAccount.bank.countryTitle')}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: convertToVwMob(8) }}>
          {isBankInfoLocked ? (
            <>
              <p style={{ ...theme.typography.contents.medium20, margin: 0, color: theme.colors.grayScale[90] }}>
                {bankCountry === 'KR' ? t('page.myAccount.bank.krLockedIntro') : t('page.myAccount.bank.usLockedIntro')}
              </p>
              <p style={{ ...theme.typography.contents.regular20, color: theme.colors.grayScale[70], margin: 0 }}>
                {bankCountry === 'KR' ? t('page.myAccount.bank.krHint') : t('page.myAccount.bank.usHint')}
              </p>
            </>
          ) : (
            <>
              <RadioGroup>
                <RadioItem onClick={() => setBankCountry('KR')}>
                  <RadioCircle active={bankCountry === 'KR'} />
                  <RadioLabel>{t('common.countries.KR')}</RadioLabel>
                </RadioItem>
                <RadioItem onClick={() => setBankCountry('US')}>
                  <RadioCircle active={bankCountry === 'US'} />
                  <RadioLabel>{t('common.countries.US')}</RadioLabel>
                </RadioItem>
              </RadioGroup>
              <p style={{ ...theme.typography.contents.regular20, color: theme.colors.grayScale[70], margin: 0 }}>
                {bankCountry === 'KR' ? t('page.myAccount.bank.krHint') : t('page.myAccount.bank.usHint')}
              </p>
            </>
          )}
        </div>
      </div>

      {bankCountry === 'KR' && isBankInfoLocked ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: convertToVwMob(12) }}>
          <p style={{ ...theme.typography.contents.medium20, margin: 0 }}>{t('page.myAccount.bank.infoTitle')}</p>
          <InputList>
            <InputGroup>
              <InputBox disabled>
                <InputLabel>{t('page.myAccount.labels.bankName')}</InputLabel>
                <InputValue disabled>{bankName || t('page.myAccount.labels.bankName')}</InputValue>
              </InputBox>
            </InputGroup>
            <InputGroup>
              <InputBox disabled>
                <InputLabel>{t('page.myAccount.labels.accountNumber')}</InputLabel>
                <InputValue disabled>{accountNumber || t('page.myAccount.labels.accountNumber')}</InputValue>
              </InputBox>
            </InputGroup>
            <div style={{ display: 'flex', flexDirection: 'column', gap: convertToVwMob(8) }}>
              <InputGroup>
                <InputBox disabled>
                  <InputLabel>{t('page.myAccount.labels.accountHolder')}</InputLabel>
                  <InputValue disabled>{accountHolder || t('page.myAccount.labels.accountHolder')}</InputValue>
                </InputBox>
              </InputGroup>
              <p style={{ ...theme.typography.contents.regular20, color: theme.colors.grayScale[70], margin: 0 }}>{t('page.myAccount.bank.nameHelp')}</p>
            </div>
          </InputList>
        </div>
      ) : bankCountry === 'KR' ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: convertToVwMob(12) }}>
          <p style={{ ...theme.typography.contents.medium20, margin: 0 }}>{t('page.myAccount.bank.infoTitle')}</p>
          <InputList>
            <InputGroup>
              <InputBox active={isBankDropdownOpen} error={showErrors && errors.bankName} onClick={() => setIsBankDropdownOpen(true)}>
                <InputLabel>{t('page.myAccount.labels.bankName')}</InputLabel>
                <InputValue placeholder={!bankName}>{bankName || t('page.myAccount.placeholders.bankName')}</InputValue>
                <IconWrapper rotate={isBankDropdownOpen}>
                  <img src={dropdownIcon} alt="dropdown" />
                </IconWrapper>
              </InputBox>
              {showErrors && errors.bankName && <ErrorText>{t('common.errors.required')}</ErrorText>}
            </InputGroup>
            <InputGroup>
              <InputBox error={showErrors && errors.accountNumber}>
                <InputLabel>{t('page.myAccount.labels.accountNumber')}</InputLabel>
                <StyledInput
                  placeholder={t('page.myAccount.placeholders.accountNumber')}
                  value={accountNumber}
                  onChange={(e) => {
                    setAccountNumber(e.target.value.replace(/[^0-9]/g, ''));
                    if (errors.accountNumber) setErrors({ ...errors, accountNumber: false });
                  }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  type="tel"
                />
                {accountNumber && (
                  <IconWrapper onClick={() => setAccountNumber('')}>
                    <img src={deleteIcon} alt="clear" />
                  </IconWrapper>
                )}
              </InputBox>
              {showErrors && errors.accountNumber && <ErrorText>{t('common.errors.required')}</ErrorText>}
            </InputGroup>
            <div style={{ display: 'flex', flexDirection: 'column', gap: convertToVwMob(8) }}>
              <InputGroup>
                <InputBox error={showErrors && errors.accountHolder}>
                  <InputLabel>{t('page.myAccount.labels.accountHolder')}</InputLabel>
                  <StyledInput
                    placeholder={t('page.myAccount.placeholders.accountHolder')}
                    value={accountHolder}
                    onChange={(e) => {
                      setAccountHolder(e.target.value);
                      if (errors.accountHolder) setErrors({ ...errors, accountHolder: false });
                    }}
                  />
                </InputBox>
                {showErrors && errors.accountHolder && <ErrorText>{t('common.errors.required')}</ErrorText>}
              </InputGroup>
              <p style={{ ...theme.typography.contents.regular20, color: theme.colors.grayScale[70], margin: 0 }}>{t('page.myAccount.bank.nameHelp')}</p>
            </div>
          </InputList>
        </div>
      ) : isBankInfoLocked ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: convertToVwMob(12) }}>
          <InputList>
            {usTransferMethod === 'zelle' ? (
              <InputGroup>
                <InputBox disabled>
                  <InputLabel>{t('page.myAccount.bank.zelleContact')}</InputLabel>
                  <InputValue disabled>{zelleContact}</InputValue>
                </InputBox>
              </InputGroup>
            ) : (
              <>
                <InputGroup>
                  <InputBox disabled>
                    <InputLabel>{t('page.myAccount.bank.payeeName')}</InputLabel>
                    <InputValue disabled>{accountHolder}</InputValue>
                  </InputBox>
                </InputGroup>
                <InputGroup>
                  <InputBox disabled>
                    <InputLabel>Street Address</InputLabel>
                    <InputValue disabled>{streetAddress}</InputValue>
                  </InputBox>
                </InputGroup>
                <InputGroup>
                  <InputBox disabled>
                    <InputLabel>City</InputLabel>
                    <InputValue disabled>{city}</InputValue>
                  </InputBox>
                </InputGroup>
                <InputGroup>
                  <InputBox disabled>
                    <InputLabel>State</InputLabel>
                    <InputValue disabled>{state ? formatUsStateLabel(state) : ''}</InputValue>
                  </InputBox>
                </InputGroup>
                <InputGroup>
                  <InputBox disabled>
                    <InputLabel>ZIP Code</InputLabel>
                    <InputValue disabled>{zipCode}</InputValue>
                  </InputBox>
                </InputGroup>
              </>
            )}
          </InputList>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: convertToVwMob(12) }}>
          <p style={{ ...theme.typography.contents.medium20, margin: 0 }}>{t('page.myAccount.bank.transferTitle')}</p>
          <RadioGroup>
            <RadioItem onClick={() => setUsTransferMethod('check')}>
              <RadioCircle active={usTransferMethod === 'check'} />
              <RadioLabel>{t('page.myAccount.bank.check')}</RadioLabel>
            </RadioItem>
            <RadioItem onClick={() => setUsTransferMethod('zelle')}>
              <RadioCircle active={usTransferMethod === 'zelle'} />
              <RadioLabel>{t('page.myAccount.bank.zelle')}</RadioLabel>
            </RadioItem>
          </RadioGroup>
          {usTransferMethod === 'zelle' ? (
            <InputList>
              <InputGroup>
                <InputBox error={showErrors && errors.zelleContact}>
                  <InputLabel>{t('page.myAccount.bank.zelleContact')}</InputLabel>
                  <StyledInput
                    placeholder={t('page.myAccount.bank.zelleContact')}
                    value={zelleContact}
                    onChange={(e) => {
                      setZelleContact(e.target.value);
                      if (errors.zelleContact) setErrors({ ...errors, zelleContact: false });
                    }}
                  />
                </InputBox>
                {showErrors && errors.zelleContact && <ErrorText>{t('common.errors.required')}</ErrorText>}
              </InputGroup>
            </InputList>
          ) : (
            <InputList>
              <InputGroup>
                <InputBox error={showErrors && errors.accountHolder}>
                  <InputLabel>{t('page.myAccount.bank.payeeName')}</InputLabel>
                  <StyledInput
                    placeholder={t('page.myAccount.bank.payeeName')}
                    value={accountHolder}
                    onChange={(e) => {
                      setAccountHolder(e.target.value);
                      if (errors.accountHolder) setErrors({ ...errors, accountHolder: false });
                    }}
                  />
                </InputBox>
                {showErrors && errors.accountHolder && <ErrorText>{t('common.errors.required')}</ErrorText>}
              </InputGroup>
              <InputGroup>
                <InputBox error={showErrors && errors.streetAddress}>
                  <InputLabel>Street Address</InputLabel>
                  <StyledInput
                    placeholder="Street Address"
                    value={streetAddress}
                    onChange={(e) => {
                      setStreetAddress(e.target.value);
                      if (errors.streetAddress) setErrors({ ...errors, streetAddress: false });
                    }}
                  />
                </InputBox>
                {showErrors && errors.streetAddress && <ErrorText>{t('common.errors.required')}</ErrorText>}
              </InputGroup>
              <InputGroup>
                <InputBox error={showErrors && errors.city}>
                  <InputLabel>City</InputLabel>
                  <StyledInput
                    placeholder="City"
                    value={city}
                    onChange={(e) => {
                      setCity(e.target.value);
                      if (errors.city) setErrors({ ...errors, city: false });
                    }}
                  />
                </InputBox>
                {showErrors && errors.city && <ErrorText>{t('common.errors.required')}</ErrorText>}
              </InputGroup>
              <InputGroup>
                <InputBox active={isStateDropdownOpen} error={showErrors && errors.state} onClick={() => setIsStateDropdownOpen(true)}>
                  <InputLabel>State</InputLabel>
                  <InputValue placeholder={!state}>{state ? formatUsStateLabel(state) : 'State'}</InputValue>
                  <IconWrapper rotate={isStateDropdownOpen}>
                    <img src={dropdownIcon} alt="dropdown" />
                  </IconWrapper>
                </InputBox>
                {showErrors && errors.state && <ErrorText>{t('common.errors.required')}</ErrorText>}
              </InputGroup>
              <InputGroup>
                <InputBox error={showErrors && errors.zipCode}>
                  <InputLabel>ZIP Code</InputLabel>
                  <StyledInput
                    placeholder="ZIP Code"
                    value={zipCode}
                    onChange={(e) => {
                      setZipCode(e.target.value.replace(/[^0-9]/g, ''));
                      if (errors.zipCode) setErrors({ ...errors, zipCode: false });
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    type="tel"
                  />
                </InputBox>
                {showErrors && errors.zipCode && <ErrorText>{t('common.errors.required')}</ErrorText>}
              </InputGroup>
            </InputList>
          )}
        </div>
      )}

      {bankCountry === 'KR' && isBankInfoLocked && isBankbookMarkedUploadedOnProfile(userProfile, bankbookFile) ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: convertToVwMob(8) }}>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', color: '#676767', fontSize: convertToVwMob(16), fontWeight: 500, lineHeight: '1.3' }}>
            <p>{t('page.myAccount.labels.bankbookCopy')}</p>
          </div>
          <BankbookUploadedPanel>
            <BankbookUploadedCheckSvg size={convertToVwMob(75)} />
            <BankbookUploadedMessage>{t('page.myAccount.bank.bankbookUploaded')}</BankbookUploadedMessage>
          </BankbookUploadedPanel>
        </div>
      ) : null}
      {bankCountry === 'KR' && isBankInfoLocked && !isBankbookMarkedUploadedOnProfile(userProfile, bankbookFile) ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: convertToVwMob(8) }}>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', color: '#676767', fontSize: convertToVwMob(16), fontWeight: 500, lineHeight: '1.3' }}>
            <p>{t('page.myAccount.labels.bankbookCopy')}</p>
          </div>
          <input
            type="file"
            id="bankbook-upload-supplemental-mob"
            style={{ display: 'none' }}
            accept="image/*,application/pdf"
            onChange={(e) => handleFileChange(e, setBankbookFile, 'hasBankbookCopy', 'bankbook')}
          />
          <UploadBox
            error={showErrors && errors.hasBankbookCopy}
            filled={!!bankbookFile}
            onClick={() => triggerFileUpload('bankbook-upload-supplemental-mob', !!bankbookFile)}
          >
            <UploadEmptyState
              text={t('page.myAccount.bank.bankbookVerificationHint')}
              subText="PDF, JPG, PNG 파일 · 최대 5MB"
              showWarning={showMissingDocIconOnFileSelect && !bankbookFile}
              layout="long"
            />
          </UploadBox>
          {bankbookFile && (
            <UploadedFileRow>
              <UploadedFileBox>
                <UploadedFileLeft>
                  <div style={{ width: convertToVwMob(24), height: convertToVwMob(24), flexShrink: 0 }}>
                    <FileItemIconSvg />
                  </div>
                  <UploadedFileName>{getFileName(bankbookFile, fileMeta['bankbook'])}</UploadedFileName>
                </UploadedFileLeft>
                {fileMeta['bankbook']?.size && <UploadedFileSize>{fileMeta['bankbook'].size}</UploadedFileSize>}
              </UploadedFileBox>
              <FileDeleteButton
                type="button"
                onClick={() => {
                  setBankbookFile(null);
                  setFileMeta(prev => {
                    const newMeta = { ...prev };
                    delete newMeta['bankbook'];
                    return newMeta;
                  });
                  resetFileInput('bankbook-upload-supplemental-mob');
                }}
              >
                <div style={{ width: convertToVwMob(24), height: convertToVwMob(24) }}>
                  <FileDeleteIconSvg />
                </div>
              </FileDeleteButton>
            </UploadedFileRow>
          )}
          {showErrors && errors.hasBankbookCopy && <ErrorText>{t('common.errors.required')}</ErrorText>}
        </div>
      ) : null}
      {bankCountry === 'KR' && !isBankInfoLocked ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: convertToVwMob(8) }}>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', color: '#676767', fontSize: convertToVwMob(16), fontWeight: 500, lineHeight: '1.3' }}>
            <p>{t('page.myAccount.labels.bankbookCopy')}</p>
          </div>
          <input
            type="file"
            id="bankbook-upload-mob"
            style={{ display: 'none' }}
            accept="image/*,application/pdf"
            onChange={(e) => handleFileChange(e, setBankbookFile, 'hasBankbookCopy', 'bankbook')}
          />
          <UploadBox
            error={showErrors && errors.hasBankbookCopy}
            filled={!!bankbookFile}
            onClick={() => triggerFileUpload('bankbook-upload-mob', !!bankbookFile)}
          >
            <UploadEmptyState
              text={t('page.myAccount.upload.bankbook')}
              subText="PDF, JPG, PNG 파일 · 최대 5MB"
              showWarning={showMissingDocIconOnFileSelect && !bankbookFile}
              layout="long"
            />
          </UploadBox>
          {bankbookFile && (
            <UploadedFileRow>
              <UploadedFileBox>
                <UploadedFileLeft>
                  <div style={{ width: convertToVwMob(24), height: convertToVwMob(24), flexShrink: 0 }}>
                    <FileItemIconSvg />
                  </div>
                  <UploadedFileName>{getFileName(bankbookFile, fileMeta['bankbook'])}</UploadedFileName>
                </UploadedFileLeft>
                {fileMeta['bankbook']?.size && <UploadedFileSize>{fileMeta['bankbook'].size}</UploadedFileSize>}
              </UploadedFileBox>
              <FileDeleteButton
                type="button"
                onClick={() => {
                  setBankbookFile(null);
                  setFileMeta(prev => {
                    const newMeta = { ...prev };
                    delete newMeta['bankbook'];
                    return newMeta;
                  });
                  resetFileInput('bankbook-upload-mob');
                }}
              >
                <div style={{ width: convertToVwMob(24), height: convertToVwMob(24) }}>
                  <FileDeleteIconSvg />
                </div>
              </FileDeleteButton>
            </UploadedFileRow>
          )}
          {showErrors && errors.hasBankbookCopy && <ErrorText>{t('common.errors.required')}</ErrorText>}
        </div>
      ) : null}
      {!isStepMode ? (
        <TipBox>
          <TipHeader>
            <TipIcon>
              <img src={hugeIcon} alt="" />
            </TipIcon>
            <TipText
              dangerouslySetInnerHTML={{
                __html: (t('page.myAccount.tips.defaultMobile') as string).replace(/\n/g, '<br />'),
              }}
            />
          </TipHeader>
        </TipBox>
      ) : null}
    </>
  );

  const renderVerification = () => {
    const isIndividual = isIndividualUserType(userProfile?.user_type);

    return (
    <>
      {isIndividual ? (
        isVerificationLocked ? (
          <InputGroup style={{ alignSelf: 'flex-start' }}>
            <InputBox disabled>
              <InputLabel style={{ fontSize: convertToVwMob(20), color: theme.colors.grayScale[60] }}>
                {t('page.myAccount.labels.residentRegistration')}
              </InputLabel>
              <InputValue disabled style={{ fontSize: convertToVwMob(24) }}>
                {residentRegistrationNumber || t('page.myAccount.labels.residentRegistration')}
              </InputValue>
            </InputBox>
          </InputGroup>
        ) : (
        <InputGroup style={{ alignSelf: 'flex-start' }}>
          <InputBox error={showErrors && (!!errors.residentRegistration || !!errors.residentRegistrationInvalid)}>
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              {residentRegistrationNumber && (
                <InputLabel style={{ fontSize: convertToVwMob(20), color: theme.colors.grayScale[60] }}>
                  {t('page.myAccount.labels.residentRegistration')}
                </InputLabel>
              )}
              <StyledInput
                placeholder={t('page.myAccount.labels.residentRegistration')}
                value={residentRegistrationNumber}
                maxLength={13}
                onChange={(e) => {
                  setResidentRegistrationNumber(e.target.value);
                  if (errors.residentRegistration || errors.residentRegistrationInvalid) {
                    setErrors({ ...errors, residentRegistration: false, residentRegistrationInvalid: false });
                  }
                }}
                error={showErrors && (!!errors.residentRegistration || !!errors.residentRegistrationInvalid)}
                style={{ fontSize: convertToVwMob(24), padding: 0, border: 'none', background: 'transparent' }}
                inputMode="numeric"
                pattern="[0-9]*"
                type="tel"
                autoComplete="off"
              />
            </div>
            {residentRegistrationNumber && (
              <IconWrapper onClick={() => setResidentRegistrationNumber('')}>
                <img src={deleteIcon} alt="clear" />
              </IconWrapper>
            )}
          </InputBox>
          {showErrors && errors.residentRegistration && <ErrorText>{t('common.errors.required')}</ErrorText>}
          {showErrors && errors.residentRegistrationInvalid && !errors.residentRegistration && (
            <ErrorText>{t('common.errors.invalidResidentRegistration')}</ErrorText>
          )}
        </InputGroup>
        )
      ) : isVerificationLocked ? (
        <InputList>
          <InputBox disabled>
            <InputLabel>{t('page.myAccount.labels.uniqueId')}</InputLabel>
            <CheckboxGroup style={{ cursor: 'not-allowed' }} onClick={(e) => e.preventDefault()}>
              <CheckboxSquare active={!hasNoUniqueId} />
              <RadioLabel style={{ fontSize: convertToVwMob(24) }}>{t('page.myAccount.labels.uniqueIdNone')}</RadioLabel>
            </CheckboxGroup>
          </InputBox>
          <InputGroup>
            <InputBox disabled>
              <InputLabel>{t('page.myAccount.placeholders.uniqueId')}</InputLabel>
              <InputValue disabled style={{ fontSize: convertToVwMob(24) }}>
                {uniqueId || t('page.myAccount.placeholders.uniqueId')}
              </InputValue>
            </InputBox>
          </InputGroup>
        </InputList>
      ) : (
        <InputList>
          <InputBox>
            <InputLabel>{t('page.myAccount.labels.uniqueId')}</InputLabel>
            <CheckboxGroup onClick={() => {
              setHasNoUniqueId(!hasNoUniqueId);
              if (!hasNoUniqueId) {
                setErrors({ ...errors, uniqueId: false, hasIdCopy: false });
              }
            }}>
              <CheckboxSquare active={!hasNoUniqueId} />
              <RadioLabel style={{ fontSize: convertToVwMob(24) }}>{t('page.myAccount.labels.uniqueIdNone')}</RadioLabel>
            </CheckboxGroup>
          </InputBox>
          <InputGroup>
            <InputBox error={showErrors && errors.uniqueId} disabled={hasNoUniqueId}>
              <StyledInput
                placeholder={t('page.myAccount.placeholders.uniqueId')}
                value={uniqueId}
                onChange={(e) => {
                  setUniqueId(e.target.value.replace(/[^0-9]/g, ''));
                  if (errors.uniqueId) setErrors({ ...errors, uniqueId: false });
                }}
                disabled={hasNoUniqueId}
                error={showErrors && errors.uniqueId}
                inputMode="numeric"
                pattern="[0-9]*"
                type="tel"
              />
              {uniqueId && !hasNoUniqueId && (
                <IconWrapper onClick={() => setUniqueId('')}>
                  <img src={deleteIcon} alt="clear" />
                </IconWrapper>
              )}
            </InputBox>
            {showErrors && errors.uniqueId && <ErrorText>{t('common.errors.required')}</ErrorText>}
          </InputGroup>
        </InputList>
      )}
      {isRegistrationCardMarkedUploadedOnProfile(userProfile, idFile) ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: convertToVwMob(8) }}>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', color: '#676767', fontSize: convertToVwMob(16), fontWeight: 500, lineHeight: '1.3' }}>
            <p>{isIndividual ? t('page.myAccount.labels.idCopy') : t('page.myAccount.labels.uniqueIdCopy')}</p>
          </div>
          <BankbookUploadedPanel>
            <BankbookUploadedCheckSvg size={convertToVwMob(75)} />
            <BankbookUploadedMessage>{t('page.myAccount.verification.idCopyUploaded')}</BankbookUploadedMessage>
          </BankbookUploadedPanel>
        </div>
      ) : null}
      {!isRegistrationCardMarkedUploadedOnProfile(userProfile, idFile) &&
      isVerificationLocked &&
      userProfile &&
      profileNeedsRegistrationCard(userProfile) ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: convertToVwMob(8) }}>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', color: '#676767', fontSize: convertToVwMob(16), fontWeight: 500, lineHeight: '1.3' }}>
            <p>{isIndividual ? t('page.myAccount.labels.idCopy') : t('page.myAccount.labels.uniqueIdCopy')}</p>
          </div>
          <input
            type="file"
            id="id-upload-supplemental-mob"
            style={{ display: 'none' }}
            accept="image/*,application/pdf"
            onChange={(e) => handleFileChange(e, setIdFile, 'hasIdCopy', 'idCopy')}
          />
          <UploadBox
            error={showErrors && errors.hasIdCopy}
            filled={!!idFile}
            onClick={() => triggerFileUpload('id-upload-supplemental-mob', !!idFile)}
          >
            <UploadEmptyState
              text={isIndividual ? t('page.myAccount.verification.idVerificationHint') : t('page.myAccount.upload.idCopy')}
              subText="PDF, JPG, PNG 파일 · 최대 5MB"
              showWarning={showMissingDocIconOnFileSelect && !idFile}
              layout="long"
            />
          </UploadBox>
          {idFile && (
            <UploadedFileRow>
              <UploadedFileBox>
                <UploadedFileLeft>
                  <div style={{ width: convertToVwMob(24), height: convertToVwMob(24), flexShrink: 0 }}>
                    <FileItemIconSvg />
                  </div>
                  <UploadedFileName>{getFileName(idFile, fileMeta['idCopy'])}</UploadedFileName>
                </UploadedFileLeft>
                {fileMeta['idCopy']?.size && <UploadedFileSize>{fileMeta['idCopy'].size}</UploadedFileSize>}
              </UploadedFileBox>
              <FileDeleteButton
                type="button"
                onClick={() => {
                  setIdFile(null);
                  setFileMeta(prev => {
                    const newMeta = { ...prev };
                    delete newMeta['idCopy'];
                    return newMeta;
                  });
                  resetFileInput('id-upload-supplemental-mob');
                }}
              >
                <div style={{ width: convertToVwMob(24), height: convertToVwMob(24) }}>
                  <FileDeleteIconSvg />
                </div>
              </FileDeleteButton>
            </UploadedFileRow>
          )}
          {showErrors && errors.hasIdCopy && <ErrorText>{t('common.errors.required')}</ErrorText>}
        </div>
      ) : !isRegistrationCardMarkedUploadedOnProfile(userProfile, idFile) && !isVerificationLocked ? (
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: convertToVwMob(8) }}>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', color: '#676767', fontSize: convertToVwMob(16), fontWeight: 500, lineHeight: '1.3' }}>
          <p>{isIndividual ? t('page.myAccount.labels.idCopy') : t('page.myAccount.labels.uniqueIdCopy')}</p>
        </div>
        <input
          type="file"
          id="id-upload-mob"
          style={{ display: 'none' }}
          accept="image/*,application/pdf"
          onChange={(e) => handleFileChange(e, setIdFile, 'hasIdCopy', 'idCopy')}
          disabled={!isIndividual && hasNoUniqueId}
        />
          <UploadBox
          error={showErrors && errors.hasIdCopy}
          disabled={!isIndividual && hasNoUniqueId}
          filled={!!idFile}
          onClick={() => {
            if (!isIndividual && hasNoUniqueId) return;
            triggerFileUpload('id-upload-mob', !!idFile);
          }}
        >
          <UploadEmptyState
            text={isIndividual ? t('page.myAccount.upload.residentIdCopy') : t('page.myAccount.upload.idCopy')}
            subText="PDF, JPG, PNG 파일 · 최대 5MB"
            showWarning={showMissingDocIconOnFileSelect && !idFile && !(!isIndividual && hasNoUniqueId)}
            layout="long"
            $disabled={!isIndividual && hasNoUniqueId}
          />
        </UploadBox>
        {idFile && !(!isIndividual && hasNoUniqueId) && (
          <UploadedFileRow>
            <UploadedFileBox>
              <UploadedFileLeft>
                <div style={{ width: convertToVwMob(24), height: convertToVwMob(24), flexShrink: 0 }}>
                  <FileItemIconSvg />
                </div>
                <UploadedFileName>{getFileName(idFile, fileMeta['idCopy'])}</UploadedFileName>
              </UploadedFileLeft>
              {fileMeta['idCopy']?.size && <UploadedFileSize>{fileMeta['idCopy'].size}</UploadedFileSize>}
            </UploadedFileBox>
            <FileDeleteButton
              type="button"
              onClick={() => {
                setIdFile(null);
                setFileMeta(prev => {
                  const newMeta = { ...prev };
                  delete newMeta['idCopy'];
                  return newMeta;
                });
                resetFileInput('id-upload-mob');
              }}
            >
              <div style={{ width: convertToVwMob(24), height: convertToVwMob(24) }}>
                <FileDeleteIconSvg />
              </div>
            </FileDeleteButton>
          </UploadedFileRow>
        )}
        {showErrors && errors.hasIdCopy && <ErrorText>{t('common.errors.required')}</ErrorText>}
      </div>
      ) : null}
      {isIndividual ? (
        (() => {
          const verificationTipKey = verificationStatus === 'none'
            ? 'page.myAccount.tips.verificationInitialMobile'
            : 'page.myAccount.tips.verificationMobile';
    return (
      <TipBox>
        <TipHeader>
          <TipIcon>
              <img src={hugeIcon} alt="" />
          </TipIcon>
            <TipText
              dangerouslySetInnerHTML={{
                __html: (t(verificationTipKey) as string).replace(/\n/g, '<br />'),
              }}
            />
        </TipHeader>
      </TipBox>
          );
        })()
      ) : null}
    </>
    );
  };

  return (
    <MobilePageLayout
      title={t('page.myAccount.pageTitle')}
      showBottomNav={false}
      mainPaddingBottom={MY_ACCOUNT_MOB_MAIN_PADDING_BOTTOM}
    >
      <PageContainer>
        <PageTitle>{t('page.myAccount.pageTitle')}</PageTitle>
        {isStepMode && renderStepIndicator()}
        {!isStepMode && (
        <TabMenu>
          {shouldHideVerificationTab ? (
            <TabRow>
              <TabItem
                active={activeTab === 'basic'}
                $compact
                onClick={() => setActiveTab('basic')}
                style={{
                  borderRadius: '10px 0 0 0',
                  borderRight: activeTab === 'basic' || activeTab === 'ministry' ? 'none' : `1px solid ${theme.colors.grayScale[20]}`,
                  borderTop: activeTab === 'basic' ? 'none' : `1px solid ${theme.colors.grayScale[20]}`,
                  borderLeft: activeTab === 'basic' ? 'none' : `1px solid ${theme.colors.grayScale[20]}`,
                  borderBottom: activeTab === 'basic' ? `4px solid ${theme.colors.primaryBlue[40]}` : `1px solid ${theme.colors.grayScale[20]}`,
                  backgroundColor: activeTab === 'basic' ? theme.colors.primaryBlue[10] : 'white',
                }}
              >
                <img src={activeTab === 'basic' ? profileColorIcon : profileIcon} alt="basic" />
                <p>{t('page.myAccount.tabs.basicShort')}</p>
              </TabItem>
              <TabItem
                active={activeTab === 'ministry'}
                $compact
                onClick={() => setActiveTab('ministry')}
                style={{
                  borderRadius: 0,
                  borderRight: activeTab === 'ministry' || activeTab === 'bank' ? 'none' : `1px solid ${theme.colors.grayScale[20]}`,
                  borderTop: activeTab === 'ministry' ? 'none' : `1px solid ${theme.colors.grayScale[20]}`,
                  borderLeft: 'none',
                  borderBottom: activeTab === 'ministry' ? `4px solid ${theme.colors.primaryBlue[40]}` : `1px solid ${theme.colors.grayScale[20]}`,
                  backgroundColor: activeTab === 'ministry' ? theme.colors.primaryBlue[10] : 'white',
                }}
              >
                <img src={activeTab === 'ministry' ? buildingColorIcon : buildingIcon} alt="ministry" />
                <p>{t('page.myAccount.tabs.ministryShort')}</p>
              </TabItem>
              <TabItem
                active={activeTab === 'bank'}
                $compact
                onClick={() => setActiveTab('bank')}
                style={{
                  borderRadius: '0 10px 0 0',
                  borderRight: activeTab === 'bank' ? 'none' : `1px solid ${theme.colors.grayScale[20]}`,
                  borderTop: activeTab === 'bank' ? 'none' : `1px solid ${theme.colors.grayScale[20]}`,
                  borderLeft: 'none',
                  borderBottom: activeTab === 'bank' ? `4px solid ${theme.colors.primaryBlue[40]}` : `1px solid ${theme.colors.grayScale[20]}`,
                  backgroundColor: activeTab === 'bank' ? theme.colors.primaryBlue[10] : 'white',
                }}
              >
                <img src={activeTab === 'bank' ? bankColorIcon : bankIcon} alt="bank" />
                <TabTextRow>
                  <p>{t('page.myAccount.tabs.bankShort')}</p>
                  {showBankTabSettlementWarning ? (
                    <MobTabSettlementWarnIcon src={noYellowTabIcon} alt="" aria-hidden />
                  ) : null}
                </TabTextRow>
              </TabItem>
            </TabRow>
          ) : (
          <>
          <TabRow>
            <TabItem
              active={activeTab === 'basic'}
              $enAlignStart={isEnMyAccountTabs}
              onClick={() => setActiveTab('basic')}
              style={{
                borderRadius: '10px 0 0 0',
                borderRight: (activeTab === 'basic' || activeTab === 'ministry') ? 'none' : `1px solid ${theme.colors.grayScale[20]}`,
                borderTop: activeTab === 'basic' ? `none` : `1px solid ${theme.colors.grayScale[20]}`,
                borderLeft: activeTab === 'basic' ? `none` : `1px solid ${theme.colors.grayScale[20]}`,
                borderBottom: activeTab === 'basic' ? `4px solid ${theme.colors.primaryBlue[40]}` : `1px solid ${theme.colors.grayScale[20]}`,
                backgroundColor: activeTab === 'basic' ? theme.colors.primaryBlue[10] : 'white',
              }}
            >
              <img src={activeTab === 'basic' ? profileColorIcon : profileIcon} alt="basic" />
              <p>{t('page.myAccount.tabs.basic')}</p>
            </TabItem>
            <TabItem 
              active={activeTab === 'ministry'}
              $enAlignStart={isEnMyAccountTabs}
              onClick={() => setActiveTab('ministry')}
              style={{
                borderRadius: '0 10px 0 0',
                borderRight: activeTab === 'ministry' ? 'none' : `1px solid ${theme.colors.grayScale[20]}`,
                borderTop: activeTab === 'ministry' ? `none` : `1px solid ${theme.colors.grayScale[20]}`,
                borderLeft: 'none',
                borderBottom: activeTab === 'ministry' ? `4px solid ${theme.colors.primaryBlue[40]}` : `1px solid ${theme.colors.grayScale[20]}`,
                backgroundColor: activeTab === 'ministry' ? theme.colors.primaryBlue[10] : 'white',
              }}
            >
              <img src={activeTab === 'ministry' ? buildingColorIcon : buildingIcon} alt="ministry" />
              <p>{t('page.myAccount.tabs.ministry')}</p>
            </TabItem>
          </TabRow>
          <TabRow>
            <TabItem
              active={activeTab === 'bank'}
              $enAlignStart={isEnMyAccountTabs}
              onClick={() => setActiveTab('bank')}
              style={{
                borderRight: (activeTab === 'bank' || activeTab === 'verification') ? 'none' : `1px solid ${theme.colors.grayScale[20]}`,
                borderBottom: activeTab === 'bank' ? `4px solid ${theme.colors.primaryBlue[40]}` : `1px solid ${theme.colors.grayScale[20]}`,
                backgroundColor: activeTab === 'bank' ? theme.colors.primaryBlue[10] : 'white',
                borderLeft: activeTab === 'bank' ? 'none' : `1px solid ${theme.colors.grayScale[20]}`,
              }}
            >
              <img src={activeTab === 'bank' ? bankColorIcon : bankIcon} alt="bank" />
              <TabTextRow $fillCell={isEnMyAccountTabs}>
                <p>{t('page.myAccount.tabs.bankShort')}</p>
                {showBankTabSettlementWarning ? (
                  <MobTabSettlementWarnIcon src={noYellowTabIcon} alt="" aria-hidden />
                ) : null}
              </TabTextRow>
            </TabItem>
            <TabItem
              active={activeTab === 'verification'}
              $enAlignStart={isEnMyAccountTabs}
              onClick={() => setActiveTab('verification')}
              style={{
                borderRight: activeTab === 'verification' ? 'none' : `1px solid ${theme.colors.grayScale[20]}`,
                borderBottom: activeTab === 'verification' ? `4px solid ${theme.colors.primaryBlue[40]}` : `1px solid ${theme.colors.grayScale[20]}`,
                backgroundColor: activeTab === 'verification' ? theme.colors.primaryBlue[10] : 'white',
              }}
            >
              <img src={activeTab === 'verification' ? securityColorIcon : securityIcon} alt="verification" />
              <TabTextRow $fillCell={isEnMyAccountTabs}>
                <p>{t('page.myAccount.tabs.verificationShort')}</p>
                {showVerificationTabSettlementWarning ? (
                  <MobTabSettlementWarnIcon src={noYellowTabIcon} alt="" aria-hidden />
                ) : null}
              </TabTextRow>
            </TabItem>
          </TabRow>
          </>
          )}
        </TabMenu>
        )}
        <SectionContainer>
          {activeTab === 'basic' && renderBasicInfo()}
          {activeTab === 'ministry' && renderMinistryInfo()}
          {activeTab === 'bank' && renderBankInfo()}
          {activeTab === 'verification' && renderVerification()}
        </SectionContainer>
      </PageContainer>

      <BottomButtonGroup>
        <ButtonRow>
          {isStepMode ? (
            <>
              <Button
                onClick={
                  activeTab === 'basic'
                    ? () => {
                        pendingCancelHandlerRef.current = handleClearAll;
                        handleRequestCancel();
                      }
                    : handlePrev
                }
                disabled={isSaving}
              >
                {activeTab === 'basic' ? t('common.actions.cancel') : t('common.actions.previous')}
              </Button>
              <Button
                primary
                $loading={isSaving}
                onClick={() => {
                  handleNext();
                }}
                disabled={isSaving}
              >
                {isSaving ? (
                  <ButtonLoadingIcon src={loadingLottie} autoplay loop aria-hidden style={{ width: 12, height: 12 }} />
                ) : activeTab === 'verification' || (activeTab === 'bank' && bankCountry === 'US') ? t('common.actions.save') : t('common.actions.next')}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  pendingCancelHandlerRef.current = handleCancel;
                  if (isDirty) {
                    setIsCancelConfirmOpen(true);
                  } else {
                    handleCancel();
                  }
                }}
                disabled={isSaving}
              >
                {t('common.actions.cancel')}
              </Button>
              <Button primary $loading={isSaving} onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <ButtonLoadingIcon src={loadingLottie} autoplay loop aria-hidden style={{ width: 12, height: 12 }} />
                ) : t('common.actions.save')}
              </Button>
            </>
          )}
        </ButtonRow>
      </BottomButtonGroup>

      {isSubmitModalOpen && (
        <ModalOverlay onClick={() => setIsSubmitModalOpen(false)}>
          <ModalContainer onClick={e => e.stopPropagation()}>
            <ModalTitle>{t('page.myAccount.modal.submitTitle')}</ModalTitle>
            <ModalDesc>
              <p>{t('page.myAccount.modal.submitLine1')}</p>
              <p>{t('page.myAccount.modal.submitLine2')}</p>
            </ModalDesc>
            <ModalButtonGroup>
              <ModalButton onClick={() => { setIsSubmitModalOpen(false); navigate(PATHS.PROJECT_MANAGEMENT); }}>{t('page.myAccount.modal.submitConfirm')}</ModalButton>
              <ModalButton primary onClick={() => { setIsSubmitModalOpen(false); navigate(PATHS.NEW_PROJECT); }}>{t('page.myAccount.modal.createProject')}</ModalButton>
            </ModalButtonGroup>
          </ModalContainer>
        </ModalOverlay>
      )}

      <DSModal
        open={isSettlementDocsCompleteModalOpen}
        size="compact"
        title={t('common.settlementDocumentsSubmittedModal.title')}
        description={
          <>
            {t('common.settlementDocumentsSubmittedModal.description1')}
            {String(t('common.settlementDocumentsSubmittedModal.description2') ?? '').trim() ? (
              <>
                <br />
                {t('common.settlementDocumentsSubmittedModal.description2')}
              </>
            ) : null}
          </>
        }
        descriptionVariant="body16"
        onClose={() => setIsSettlementDocsCompleteModalOpen(false)}
        actions={[
          {
            label: t('common.actions.confirm'),
            variant: 'primary',
            onClick: () => setIsSettlementDocsCompleteModalOpen(false),
          },
        ]}
      />

      <DSModal
        open={isCancelConfirmOpen}
        size="compact"
        title={t('page.myAccount.modal.cancelConfirmTitle')}
        description={t('page.myAccount.modal.cancelConfirmDesc')}
        onClose={() => setIsCancelConfirmOpen(false)}
        actions={[
          { label: t('page.myAccount.modal.cancelConfirm'), variant: 'secondary', onClick: handleConfirmCancel },
          { label: t('page.myAccount.modal.continueInput'), variant: 'primary', onClick: () => setIsCancelConfirmOpen(false) },
        ]}
      />

      {/* Bottom Sheets */}
      <DSBottomSheet
        open={isCountryDropdownOpen}
        title={t('page.myAccount.labels.country')}
        onClose={() => setIsCountryDropdownOpen(false)}
      >
        <BottomSheetScrollable>
          {countryOptions.map(c => (
            <BottomSheetItem
              key={c.value}
              active={country === c.value}
              onClick={() => {
                setCountry(c.value);
                setIsCountryDropdownOpen(false);
                if (errors.country) setErrors({ ...errors, country: false });
              }}
            >
              {c.label}
            </BottomSheetItem>
          ))}
        </BottomSheetScrollable>
      </DSBottomSheet>

      <DSBottomSheet
        open={isOrganizationDropdownOpen}
        title={t('page.myAccount.labels.organizationInstitution')}
        onClose={() => setIsOrganizationDropdownOpen(false)}
      >
        <BottomSheetScrollable>
          {organizationOptions.map((opt) => (
            <BottomSheetItem
              key={`${opt.code}-${opt.name}`}
              active={organization === opt.code}
              onClick={() => {
                setOrganization(opt.code);
                if (opt.code !== ORGANIZATION_ETC_CODE) setOrganizationOther('');
                setIsOrganizationDropdownOpen(false);
                if (errors.organization) setErrors({ ...errors, organization: false, organizationOther: false });
              }}
            >
              {opt.name}
            </BottomSheetItem>
          ))}
        </BottomSheetScrollable>
      </DSBottomSheet>

      <DSBottomSheet
        open={isLanguageDropdownOpen}
        title={t('page.myAccount.labels.language')}
        onClose={() => setIsLanguageDropdownOpen(false)}
      >
        <BottomSheetScrollable>
          {languages.map(lang => (
            <BottomSheetItem
              key={lang}
              active={language === lang}
              onClick={() => {
                setLanguage(lang);
                setIsLanguageDropdownOpen(false);
                if (errors.language) setErrors({ ...errors, language: false });
              }}
            >
              {lang}
            </BottomSheetItem>
          ))}
        </BottomSheetScrollable>
      </DSBottomSheet>

      <DSBottomSheet
        open={isMinistryCountryDropdownOpen}
        title={t('page.myAccount.labels.ministryCountry')}
        onClose={() => setIsMinistryCountryDropdownOpen(false)}
      >
        <BottomSheetScrollable>
          {countryOptions.map(c => (
            <BottomSheetItem
              key={c.value}
              active={ministryCountry === c.value}
              onClick={() => {
                setMinistryCountry(c.value);
                setIsMinistryCountryDropdownOpen(false);
                if (errors.ministryCountry) setErrors({ ...errors, ministryCountry: false });
              }}
            >
              {c.label}
            </BottomSheetItem>
          ))}
        </BottomSheetScrollable>
      </DSBottomSheet>

      <DSBottomSheet
        open={isBankDropdownOpen}
        title={t('page.myAccount.labels.bankName')}
        onClose={() => setIsBankDropdownOpen(false)}
      >
        <BottomSheetScrollable>
          {bankGroups.map((group) => (
            <React.Fragment key={group.label}>
              {group.type !== 'bank' && <DropdownGroupLabel>{group.label}</DropdownGroupLabel>}
              {group.options.map((bank) => (
                <BottomSheetItem
                  key={bank}
                  active={bankName === bank}
                  onClick={() => {
                    setBankName(bank);
                    setIsBankDropdownOpen(false);
                    if (errors.bankName) setErrors({ ...errors, bankName: false });
                  }}
                >
                  {bank}
                </BottomSheetItem>
              ))}
            </React.Fragment>
          ))}
        </BottomSheetScrollable>
      </DSBottomSheet>

      <DSBottomSheet
        open={isStateDropdownOpen}
        title="State"
        onClose={() => setIsStateDropdownOpen(false)}
      >
        <BottomSheetScrollable>
          {states.map(s => (
            <BottomSheetItem
              key={s.code}
              active={state === s.code}
              onClick={() => {
                setState(s.code);
                setIsStateDropdownOpen(false);
                if (errors.state) setErrors({ ...errors, state: false });
              }}
            >
              {`${s.name} (${s.code})`}
            </BottomSheetItem>
          ))}
        </BottomSheetScrollable>
      </DSBottomSheet>
    </MobilePageLayout>
  );
};

export default MyAccountMob;
